/**
 * 7-Role System Definitions and Utilities
 * 
 * SmartCare Platform Role Hierarchy:
 * - Developer: Full system access, can impersonate, manage everything
 * - PlatformManager: High-level platform oversight, can't impersonate
 * - OrgManager: Manage organization-level operations and homes
 * - OrgAdmin: Admin-level access within organization
 * - HomeManager: Manage single home operations
 * - HomeAdmin: Admin-level access within home
 * - HomeStaff: Basic audit completion tasks
 */

export type Role = 
  | 'Developer'
  | 'PlatformManager' 
  | 'OrgManager'
  | 'OrgAdmin'
  | 'HomeManager'
  | 'HomeAdmin'
  | 'HomeStaff';

export type UserStatus = 'Active' | 'Pending' | 'Suspended';

// Role hierarchy levels for comparison (higher number = more permissions)
export const ROLE_HIERARCHY: Record<Role, number> = {
  Developer: 100,
  PlatformManager: 80,
  OrgManager: 60,
  OrgAdmin: 50,
  HomeManager: 40,
  HomeAdmin: 30,
  HomeStaff: 10
};

// Role categories for permission grouping
export const ROLE_CATEGORIES = {
  PLATFORM: ['Developer', 'PlatformManager'] as Role[],
  ORGANIZATION: ['OrgManager', 'OrgAdmin'] as Role[],
  HOME: ['HomeManager', 'HomeAdmin', 'HomeStaff'] as Role[],
  MANAGEMENT: ['Developer', 'PlatformManager', 'OrgManager', 'HomeManager'] as Role[],
  ADMIN: ['Developer', 'PlatformManager', 'OrgAdmin', 'HomeAdmin'] as Role[]
};

// Helper functions for role checking
export function getRoleLevel(role: Role): number {
  return ROLE_HIERARCHY[role] || 0;
}

export function hasMinimumRole(userRole: Role, requiredRole: Role): boolean {
  return getRoleLevel(userRole) >= getRoleLevel(requiredRole);
}

export function isManagementRole(role: Role): boolean {
  return ROLE_CATEGORIES.MANAGEMENT.includes(role);
}

export function isAdminRole(role: Role): boolean {
  return ROLE_CATEGORIES.ADMIN.includes(role);
}

export function isPlatformRole(role: Role): boolean {
  return ROLE_CATEGORIES.PLATFORM.includes(role);
}

export function isOrganizationRole(role: Role): boolean {
  return ROLE_CATEGORIES.ORGANIZATION.includes(role);
}

export function isHomeRole(role: Role): boolean {
  return ROLE_CATEGORIES.HOME.includes(role);
}

// Role descriptions for UI display
export const ROLE_DESCRIPTIONS: Record<Role, { title: string; description: string; scope: string }> = {
  Developer: {
    title: 'Developer',
    description: 'Full system access, can impersonate users, manage platform features',
    scope: 'Global'
  },
  PlatformManager: {
    title: 'Platform Manager',
    description: 'High-level platform oversight, manages SmartCare operations',
    scope: 'Platform'
  },
  OrgManager: {
    title: 'Organization Manager',
    description: 'Manages organization-level operations and multiple homes',
    scope: 'Organization'
  },
  OrgAdmin: {
    title: 'Organization Admin',
    description: 'Administrative access within organization, user management',
    scope: 'Organization'
  },
  HomeManager: {
    title: 'Home Manager',
    description: 'Manages single care home operations and compliance',
    scope: 'Home'
  },
  HomeAdmin: {
    title: 'Home Admin',
    description: 'Administrative access within home, audit management',
    scope: 'Home'
  },
  HomeStaff: {
    title: 'Home Staff',
    description: 'Completes audits and basic care tasks',
    scope: 'Home'
  }
};

// Helper to normalize role names (case-insensitive, handles snake_case)
const normalizeRole = (role: Role): string => {
  return role.toLowerCase().replace(/_/g, '');
};

// Role-based permissions matrix (case-insensitive)
export const ROLE_PERMISSIONS = {
  // System-level permissions
  canImpersonate: (role: Role) => normalizeRole(role) === 'developer',
  canAccessDeveloperConsole: (role: Role) => normalizeRole(role) === 'developer',
  canManagePlatform: (role: Role) => ['developer', 'platformmanager'].includes(normalizeRole(role)),
  
  // Organization permissions
  canCreateOrganizations: (role: Role) => normalizeRole(role) === 'developer',
  canManageOrganizations: (role: Role) => ['developer', 'platformmanager'].includes(normalizeRole(role)),
  canViewAllOrganizations: (role: Role) => ['developer', 'platformmanager'].includes(normalizeRole(role)),
  
  // Home permissions
  canCreateHomes: (role: Role) => ['developer', 'platformmanager', 'orgmanager', 'parentcompanymanager'].includes(normalizeRole(role)),
  canManageHomes: (role: Role) => ['developer', 'platformmanager', 'orgmanager', 'parentcompanymanager', 'orgadmin', 'parentcompanyadmin', 'homemanager', 'homeadmin'].includes(normalizeRole(role)),
  canViewAllHomes: (role: Role) => ['developer', 'platformmanager'].includes(normalizeRole(role)),
  
  // User management permissions
  canCreateUsers: (role: Role) => ['developer', 'platformmanager', 'orgmanager', 'parentcompanymanager', 'orgadmin', 'parentcompanyadmin', 'homemanager', 'homeadmin'].includes(normalizeRole(role)),
  canManageUsers: (role: Role) => ['developer', 'platformmanager', 'orgmanager', 'parentcompanymanager', 'orgadmin', 'parentcompanyadmin', 'homemanager', 'homeadmin'].includes(normalizeRole(role)),
  canViewAllUsers: (role: Role) => ['developer', 'platformmanager'].includes(normalizeRole(role)),
  canDeleteUsers: (role: Role) => ['developer', 'platformmanager', 'orgmanager', 'parentcompanymanager'].includes(normalizeRole(role)),
  
  // Template permissions
  canCreateTemplates: (role: Role) => ['developer', 'platformmanager'].includes(normalizeRole(role)),
  canManageGlobalTemplates: (role: Role) => ['developer', 'platformmanager'].includes(normalizeRole(role)),
  canManageOrgTemplates: (role: Role) => ['developer', 'platformmanager', 'orgmanager', 'parentcompanymanager', 'orgadmin', 'parentcompanyadmin'].includes(normalizeRole(role)),
  canManageHomeTemplates: (role: Role) => ['developer', 'platformmanager', 'orgmanager', 'parentcompanymanager', 'orgadmin', 'parentcompanyadmin', 'homemanager', 'homeadmin'].includes(normalizeRole(role)),
  
  // Audit permissions
  canCreateAudits: (role: Role) => ['developer', 'platformmanager', 'orgmanager', 'orgadmin', 'homemanager', 'homeadmin', 'homestaff'].includes(normalizeRole(role)),
  canCompleteAudits: (role: Role) => ['developer', 'platformmanager', 'orgmanager', 'orgadmin', 'homemanager', 'homeadmin', 'homestaff'].includes(normalizeRole(role)),
  canApproveAudits: (role: Role) => ['developer', 'platformmanager', 'orgmanager', 'orgadmin', 'homemanager', 'homeadmin'].includes(normalizeRole(role)),
  canViewAllAudits: (role: Role) => ['developer', 'platformmanager'].includes(normalizeRole(role)),
  canDeleteAudits: (role: Role) => ['developer', 'platformmanager', 'orgmanager', 'homemanager'].includes(normalizeRole(role)),
  
  // Reporting permissions
  // Explicitly allow organization- and home-level admins/managers to view reports
  canViewReports: (role: Role) => ['developer', 'platformmanager', 'orgmanager', 'orgadmin', 'homemanager', 'homeadmin'].includes(normalizeRole(role)),
  // Export data should be available to platform, org and home level managers/admins
  canExportData: (role: Role) => ['developer', 'platformmanager', 'orgmanager', 'orgadmin', 'homemanager', 'homeadmin'].includes(normalizeRole(role))
};

// Scope-based access control helpers
export interface UserScope {
  role: Role;
  organizationId?: number | null;
  homeId?: number | null;
}

export function hasOrganizationAccess(user: UserScope, targetOrgId: number | null): boolean {
  // Platform roles have access to everything
  if (isPlatformRole(user.role)) {
    return true;
  }
  
  // For null org (standalone home), user must have exact home access
  if (targetOrgId === null) {
    return user.homeId !== null && user.organizationId === null;
  }
  
  // For organization resources, user must belong to that organization
  return user.organizationId === targetOrgId;
}

export function hasHomeAccess(user: UserScope, targetHomeId: number | null): boolean {
  // Platform roles have access to everything
  if (isPlatformRole(user.role)) {
    return true;
  }
  
  // Organization roles can access homes in their organization
  if (isOrganizationRole(user.role) && user.organizationId) {
    // This would require checking if the home belongs to the user's organization
    // Implementation depends on how you want to structure this check
    return true; // Simplified for now
  }
  
  // Home roles must have exact home match
  return user.homeId === targetHomeId;
}

/**
 * Check if a user can manage another user based on role hierarchy.
 * A user can only manage users with STRICTLY LOWER role levels.
 * 
 * Example: HomeAdmin (level 30) can manage HomeStaff (level 10)
 *          but NOT HomeManager (level 40) or higher
 */
export function canManageUserRole(managerRole: Role, targetRole: Role): boolean {
  const managerLevel = getRoleLevel(managerRole);
  const targetLevel = getRoleLevel(targetRole);
  
  // Can only manage users with strictly lower role level
  return managerLevel > targetLevel;
}

export function canEditInScope(user: UserScope, targetOrgId?: number | null, targetHomeId?: number | null): boolean {
  // Only management and admin roles can edit
  if (!isManagementRole(user.role) && !isAdminRole(user.role)) {
    return false;
  }
  
  // Check organization scope if provided
  if (targetOrgId !== undefined && !hasOrganizationAccess(user, targetOrgId)) {
    return false;
  }
  
  // Check home scope if provided
  if (targetHomeId !== undefined && !hasHomeAccess(user, targetHomeId)) {
    return false;
  }
  
  return true;
}

// Migration helper to map old roles to new roles
export function mapLegacyRole(
  oldRole: string, 
  isSuperadmin: boolean, 
  organizationId?: number | null
): Role {
  if (isSuperadmin) {
    return 'Developer';
  }
  
  switch (oldRole?.toLowerCase()) {
    case 'manager':
      return organizationId ? 'OrgManager' : 'HomeManager';
    case 'admin':
    case 'company_admin':
    case 'platform_admin':
    case 'smartcare_admin':
      return organizationId ? 'OrgAdmin' : 'HomeAdmin';
    case 'company_manager':
    case 'home_manager':
      return organizationId ? 'OrgManager' : 'HomeManager';
    case 'home_admin':
      return 'HomeAdmin';
    case 'platform_manager':
      return 'PlatformManager';
    case 'staff':
    case 'user':
    case 'smartcare_support':
    default:
      return 'HomeStaff';
  }
}